import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useAppSelector } from "../../../app/store/store";
import { calendarService, ICalendarFilter, IGetCalendarFilterOptionsResponse } from "../../../entities/calendar";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { getDefaultTrialCalendarFilter, ITrialCalendarFilter, TTrialLessonStatus, TTutorGender } from "../../../entities/trialLesson";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { MyError } from "../../../shared/lib/error/MyError";
import { IItem } from "../../../shared/model/types";
import { MyButton } from "../../../shared/ui/button";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { formatTutorLoad } from "../../../shared/lib/helpers/formatTutorLoad";
import { SurfaceBlock } from "../../../shared/ui/surfaceBlock";
import classes from './trialCalendarFilters.module.scss'

interface IProps {
    filter: ITrialCalendarFilter;
    onChange: (filter: ITrialCalendarFilter) => void;
    onOptionsChange: (options: IGetCalendarFilterOptionsResponse | null) => void;
}

type TIdsField = 'tgAccountIds' | 'tagIds' | 'userIds'
type TStage = TIdsField | null
type TStatusChoice = TTrialLessonStatus | 'lost_before_trial'
const BEFORE_TRIAL = 'TRIAL_LESSON_LOST_TYPE_BEFORE_TRIAL' as const
const getStatusChoices = (filter: ITrialCalendarFilter): TStatusChoice[] => [
    ...filter.statuses,
    ...(filter.lostTypes?.includes(BEFORE_TRIAL) ? ['lost_before_trial' as const] : []),
]

const normalizeTrialFilter = (filter: ITrialCalendarFilter): ITrialCalendarFilter => ({
    tgAccountIds: filter.tgAccountIds || [],
    userIds: filter.userIds || [],
    tagIds: filter.tagIds || [],
    statuses: filter.statuses || [],
    lostTypes: filter.lostTypes || [],
    tutorGender: filter.tutorGender || 'TUTOR_GENDER_UNSPECIFIED',
    requiredDurationSlots: Math.max(1, filter.requiredDurationSlots || 1),
})

const normalizeOptions = (
    options: IGetCalendarFilterOptionsResponse
): IGetCalendarFilterOptionsResponse => ({
    ...options,
    tgAccounts: options.tgAccounts || [],
    tutors: options.tutors || [],
    students: options.students || [],
    tags: options.tags || [],
})

const STATUS_ITEMS: Array<IItem<TStatusChoice>> = [
    {id: 'TRIAL_LESSON_STATUS_ASSIGNED', name: 'Назначенные'},
    {id: 'TRIAL_LESSON_STATUS_COMPLETED', name: 'Проведённые'}, // todo не используется смотреть TRIAL_LESSON_STATUS_ORANGE_PENDING
    {id: 'TRIAL_LESSON_STATUS_STUDENT_CREATED', name: 'Студент создан'},
    {id: 'TRIAL_LESSON_STATUS_LOST', name: 'Слиты'},
    {id: 'lost_before_trial', name: 'Слив до пробного'},
    {id: 'TRIAL_LESSON_STATUS_ORANGE_PENDING', name: 'Проведённые'},
]

const GENDER_ITEMS: Array<IItem<TTutorGender>> = [
    {id: 'TUTOR_GENDER_MALE', name: 'Мужской'},
    {id: 'TUTOR_GENDER_FEMALE', name: 'Женский'},
    {id: 'TUTOR_GENDER_ANY', name: 'Любой'},
]

const filterTutorsByGender = (
    tutors: IGetCalendarFilterOptionsResponse['tutors'],
    gender: TTutorGender,
): IGetCalendarFilterOptionsResponse['tutors'] => {
    if(gender === 'TUTOR_GENDER_MALE') return tutors.filter(tutor => tutor.gender?.toLowerCase() === 'male')
    if(gender === 'TUTOR_GENDER_FEMALE') return tutors.filter(tutor => tutor.gender?.toLowerCase() === 'female')
    if(gender === 'TUTOR_GENDER_ANY') return tutors
    return []
}

const toCalendarFilter = (filter: ITrialCalendarFilter): ICalendarFilter => ({
    tgAccountIds: filter.tgAccountIds,
    userIds: filter.userIds,
    studentIds: [],
    tagIds: filter.tagIds,
    includeCancelled: true,
    includeRescheduled: true,
})

const toItems = <T extends {id: string}>(items: T[] = [], getName: (item: T) => string): IItem<string>[] => {
    return items.map(item => ({id: item.id, name: getName(item)}))
}

const getExistingIds = (ids: string[], options: Array<{id: string}>): string[] => {
    const optionIds = new Set(options.map(option => option.id))
    return [...new Set(ids.filter(id => optionIds.has(id)))]
}

const hasSameIds = (first: string[], second: string[]): boolean => {
    return first.length === second.length && first.every(id => second.includes(id))
}

const getIdsKey = (filter: ITrialCalendarFilter): string => {
    const normalizedFilter = normalizeTrialFilter(filter)

    return [
        normalizedFilter.tgAccountIds.join(','),
        normalizedFilter.tagIds.join(','),
        normalizedFilter.tutorGender,
        normalizedFilter.userIds.join(','),
    ].join('|')
}

const constrainOptions = (
    options: IGetCalendarFilterOptionsResponse,
    filter: ITrialCalendarFilter,
): IGetCalendarFilterOptionsResponse => {
    const availableTags = options.tags || []

    return {
        ...options,
        tgAccounts: filter.tgAccountIds.length
            ? options.tgAccounts.filter(item => filter.tgAccountIds.includes(item.id))
            : options.tgAccounts,
        tutors: filter.userIds.length
            ? options.tutors.filter(item => filter.userIds.includes(item.userId))
            : options.tutors,
        students: [],
        tags: filter.tagIds.length
            ? availableTags.filter(item => filter.tagIds.includes(item.id))
            : availableTags,
    }
}

export const TrialCalendarFilters: FC<IProps> = ({filter, onChange, onOptionsChange}) => {
    const [stage, setStage] = useState<TStage>('tgAccountIds')
    const [fullFilter, setFullFilter] = useState<ITrialCalendarFilter | null>(null)
    const [isSelectingAll, setIsSelectingAll] = useState(false)
    const [tgAccountOptions, setTgAccountOptions] = useState<IGetCalendarFilterOptionsResponse['tgAccounts']>([])
    const [tagOptions, setTagOptions] = useState<IGetCalendarFilterOptionsResponse['tags']>([])
    const [allTutorOptions, setAllTutorOptions] = useState<IGetCalendarFilterOptionsResponse['tutors']>([])
    const abortControllerRef = useRef<AbortController | null>(null)
    const pendingChangeRef = useRef<{key: string; field: TIdsField} | null>(null)
    const optionsRef = useRef<IGetCalendarFilterOptionsResponse | null>(null)
    const {my} = useAppSelector(state => state.myReducer)
    const {setIsAuth} = useMyActions()
    const {setGlobalMessage} = useGlobalMessageActions()

    const idsKey = getIdsKey(filter)
    const tutorOptions = useMemo(
        () => filterTutorsByGender(allTutorOptions, normalizeTrialFilter(filter).tutorGender),
        [allTutorOptions, filter],
    )

    const getCurrentTutorUserId = (
        tutors: IGetCalendarFilterOptionsResponse['tutors'],
        selectedUserIds: string[],
    ): string | null => {
        if(my.role !== 'tutor' || selectedUserIds.length) return null

        const currentUserId = String(my.id)

        return tutors.some(tutor => tutor.userId === currentUserId)
            ? currentUserId
            : null
    }

    const setFilterOptions = (options: IGetCalendarFilterOptionsResponse | null) => {
        optionsRef.current = options
        onOptionsChange(options)
    }

    const requestOptions = async (
        selected: ITrialCalendarFilter,
        nextStage: Exclude<TStage, null>,
        signal: AbortSignal,
    ) => {
        setStage(nextStage)
        const response = await calendarService.getCalendarFilterOptions(
            {selected: toCalendarFilter(selected)},
            signal,
        )
        return normalizeOptions(response)
    }

    useEffect(() => {
        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller

        const resetInvalidFilter = (nextFilter: ITrialCalendarFilter) => {
            setFilterOptions(null)
            onChange(nextFilter)
            setGlobalMessage({
                message: 'Сохранённые фильтры пробных занятий больше не актуальны',
                type: 'error',
            })
        }

        const loadOptions = async () => {
            try{
                const pendingChange = pendingChangeRef.current?.key === idsKey
                    ? pendingChangeRef.current
                    : null
                pendingChangeRef.current = null
                const currentOptions = optionsRef.current ? normalizeOptions(optionsRef.current) : null
                const normalizedFilter = normalizeTrialFilter(filter)

                if(pendingChange && currentOptions){
                    if(pendingChange.field === 'userIds'){
                        setFilterOptions(constrainOptions({
                            ...currentOptions,
                            tgAccounts: tgAccountOptions,
                            tutors: tutorOptions,
                            students: [],
                            tags: tagOptions,
                        }, normalizedFilter))
                        return
                    }

                    if(pendingChange.field === 'tgAccountIds'){
                        setAllTutorOptions([])

                        if(!normalizedFilter.tgAccountIds.length){
                            setTagOptions([])
                            setFilterOptions({
                                ...currentOptions,
                                tgAccounts: tgAccountOptions,
                                tutors: [],
                                students: [],
                                tags: [],
                            })
                            return
                        }

                        const optionsByTg = await requestOptions(normalizedFilter, 'tagIds', controller.signal)
                        if(controller.signal.aborted) return

                        setTagOptions(optionsByTg.tags || [])
                        setFilterOptions(constrainOptions({
                            ...optionsByTg,
                            tgAccounts: tgAccountOptions,
                            tutors: [],
                            students: [],
                        }, normalizedFilter))
                        return
                    }

                    setAllTutorOptions([])

                    if(!normalizedFilter.tagIds.length){
                        setFilterOptions({
                            ...currentOptions,
                            tgAccounts: tgAccountOptions,
                            tutors: [],
                            students: [],
                            tags: tagOptions,
                        })
                        return
                    }

                    const optionsByTag = await requestOptions(normalizedFilter, 'userIds', controller.signal)
                    if(controller.signal.aborted) return

                    setAllTutorOptions(optionsByTag.tutors)
					const genderTutors = filterTutorsByGender(optionsByTag.tutors, normalizedFilter.tutorGender)
                    const currentTutorUserId = getCurrentTutorUserId(
						genderTutors,
                        normalizedFilter.userIds,
                    )
                    const nextFilter = currentTutorUserId
                        ? {...normalizedFilter, userIds: [currentTutorUserId]}
                        : {...normalizedFilter}

                    setFilterOptions(constrainOptions({
                        ...optionsByTag,
						tutors: genderTutors,
                        tgAccounts: tgAccountOptions,
                        tags: tagOptions,
                        students: [],
                    }, nextFilter))

                    if(currentTutorUserId){
                        pendingChangeRef.current = {
                            key: getIdsKey(nextFilter),
                            field: 'userIds',
                        }
                        onChange(nextFilter)
                    }
                    return
                }

                setFilterOptions(null)
                const emptyFilter: ITrialCalendarFilter = {
                    ...normalizedFilter,
                    tgAccountIds: [],
                    tagIds: [],
                    userIds: [],
                }
                const initialOptions = await requestOptions(emptyFilter, 'tgAccountIds', controller.signal)
                if(controller.signal.aborted) return

                setTgAccountOptions(initialOptions.tgAccounts)

                const tgAccountIds = getExistingIds(normalizedFilter.tgAccountIds, initialOptions.tgAccounts)
                if(!hasSameIds(normalizedFilter.tgAccountIds, tgAccountIds)){
                    resetInvalidFilter({...normalizedFilter, tgAccountIds, tagIds: [], userIds: []})
                    return
                }

                if(!tgAccountIds.length){
                    setTagOptions([])
                    setAllTutorOptions([])
                    setFilterOptions(constrainOptions({...initialOptions, students: []}, normalizedFilter))
                    return
                }

                const filterWithTg = {...normalizedFilter, tgAccountIds, tagIds: [], userIds: []}
                const optionsByTg = await requestOptions(filterWithTg, 'tagIds', controller.signal)
                if(controller.signal.aborted) return

                const availableTags = optionsByTg.tags || []
                setTagOptions(availableTags)
                const tagIds = getExistingIds(normalizedFilter.tagIds, availableTags)
                if(!hasSameIds(normalizedFilter.tagIds, tagIds)){
                    resetInvalidFilter({...normalizedFilter, tgAccountIds, tagIds, userIds: []})
                    return
                }

                if(!tagIds.length){
                    setAllTutorOptions([])
                    setFilterOptions(constrainOptions({
                        ...optionsByTg,
                        tgAccounts: initialOptions.tgAccounts,
                        students: [],
                    }, filter))
                    return
                }

                const filterWithTag = {...normalizedFilter, tgAccountIds, tagIds, userIds: []}
                const optionsByTag = await requestOptions(filterWithTag, 'userIds', controller.signal)
                if(controller.signal.aborted) return

                setAllTutorOptions(optionsByTag.tutors)
				const genderTutors = filterTutorsByGender(optionsByTag.tutors, normalizedFilter.tutorGender)
                let userIds = getExistingIds(
                    normalizedFilter.userIds,
					genderTutors.map(tutor => ({id: tutor.userId})),
                )
                const currentTutorUserId = getCurrentTutorUserId(
					genderTutors,
                    normalizedFilter.userIds,
                )

                if(currentTutorUserId){
                    userIds = [currentTutorUserId]
                }

                if(normalizedFilter.userIds.length && !hasSameIds(normalizedFilter.userIds, userIds)){
                    resetInvalidFilter({...normalizedFilter, tgAccountIds, tagIds, userIds})
                    return
                }

                const validatedFilter = {...normalizedFilter, tgAccountIds, tagIds, userIds}

                setFilterOptions(constrainOptions({
                    ...optionsByTag,
					tutors: genderTutors,
                    tgAccounts: initialOptions.tgAccounts,
                    tags: availableTags,
                    students: [],
                }, validatedFilter))

                if(!hasSameIds(filter.userIds, userIds)){
                    pendingChangeRef.current = {
                        key: getIdsKey(validatedFilter),
                        field: 'userIds',
                    }
                    onChange(validatedFilter)
                }
            }
            catch(error){
                if(error instanceof DOMException && error.name === 'AbortError') return

                console.log(error)
                setFilterOptions(null)
                if(error instanceof AuthError){
                    setIsAuth(false)
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else if(error instanceof MyError){
                    setGlobalMessage({message: error.message, type: 'error'})
                }
                else{
                    setGlobalMessage({message: 'Ошибка при получении фильтров пробных занятий', type: 'error'})
                }
            }
            finally{
                if(abortControllerRef.current === controller) setStage(null)
            }
        }

        loadOptions()
        return () => controller.abort()
        // Запрос зависит только от выбранных ID. Изменение статусов не меняет списки фильтров.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idsKey, my.id, my.role])

    const changeSelectedIds = (field: TIdsField, ids: string[]) => {
        const normalizedFilter = normalizeTrialFilter(filter)
        let nextFilter: ITrialCalendarFilter

        if(field === 'tgAccountIds'){
			nextFilter = {
				...normalizedFilter,
				tgAccountIds: ids,
				tagIds: [],
				userIds: [],
				tutorGender: 'TUTOR_GENDER_UNSPECIFIED',
			}
        }
        else if(field === 'tagIds'){
			nextFilter = {
				...normalizedFilter,
				tagIds: ids,
				userIds: [],
				tutorGender: 'TUTOR_GENDER_UNSPECIFIED',
			}
        }
        else{
            nextFilter = {...normalizedFilter, userIds: ids}
        }

        pendingChangeRef.current = {key: getIdsKey(nextFilter), field}
        onChange(nextFilter)
    }

    const onSelected = (field: TIdsField) => (item: IItem<string>) => (selected: boolean) => {
        const currentIds = normalizeTrialFilter(filter)[field]
        const ids = selected
            ? [...currentIds, item.id]
            : currentIds.filter(id => id !== item.id)

        changeSelectedIds(field, ids)
    }

    const onSelectAll = (field: TIdsField) => (ids: string[]) => {
        changeSelectedIds(field, ids)
    }

	const changeTutorGender = (gender: TTutorGender) => {
		const nextFilter: ITrialCalendarFilter = {
			...normalizeTrialFilter(filter),
			tutorGender: gender,
			userIds: [],
		}
		pendingChangeRef.current = {key: getIdsKey(nextFilter), field: 'userIds'}
		onChange(nextFilter)
	}

    const changeStatuses = (choices: TStatusChoice[]) => {
        if(choices.length) onChange({
            ...normalizeTrialFilter(filter),
            statuses: choices.filter((choice): choice is TTrialLessonStatus => choice !== 'lost_before_trial'),
            lostTypes: choices.includes('lost_before_trial') ? [BEFORE_TRIAL] : [],
        })
    }

    const onStatusSelected = (item: IItem<TStatusChoice>) => (selected: boolean) => {
        const currentChoices = getStatusChoices(normalizeTrialFilter(filter))
        const statuses = selected
            ? [...currentChoices, item.id]
            : currentChoices.filter(status => status !== item.id)

        changeStatuses(statuses)
    }

    const selectAllFilters = async () => {
        if(stage !== null || isSelectingAll) return

        abortControllerRef.current?.abort()
        const controller = new AbortController()
        abortControllerRef.current = controller
        setIsSelectingAll(true)

        try{
            const emptyFilter = getDefaultTrialCalendarFilter()
            const initialOptions = normalizeOptions(
                await calendarService.getCalendarFilterOptions(
                    {selected: toCalendarFilter(emptyFilter)},
                    controller.signal,
                )
            )
            if(controller.signal.aborted) return

            const tgAccountIds = initialOptions.tgAccounts.map(item => item.id)
            const filterWithTg: ITrialCalendarFilter = {
                ...emptyFilter,
                tgAccountIds,
            }
            const optionsByTg = tgAccountIds.length
                ? normalizeOptions(
                    await calendarService.getCalendarFilterOptions(
                        {selected: toCalendarFilter(filterWithTg)},
                        controller.signal,
                    )
                )
                : initialOptions
            if(controller.signal.aborted) return

            const tagIds = optionsByTg.tags.map(item => item.id)
            const filterWithTags: ITrialCalendarFilter = {
                ...filterWithTg,
                tagIds,
            }
            const optionsByTags = tagIds.length
                ? normalizeOptions(
                    await calendarService.getCalendarFilterOptions(
                        {selected: toCalendarFilter(filterWithTags)},
                        controller.signal,
                    )
                )
                : {...optionsByTg, tutors: []}
            if(controller.signal.aborted) return

            const fullFilter: ITrialCalendarFilter = {
                ...filterWithTags,
                userIds: optionsByTags.tutors.map(item => item.userId),
                statuses: getDefaultTrialCalendarFilter().statuses,
                lostTypes: [],
                tutorGender: 'TUTOR_GENDER_ANY',
            }

            setTgAccountOptions(initialOptions.tgAccounts)
            setTagOptions(optionsByTg.tags)
            setAllTutorOptions(optionsByTags.tutors)
            setFullFilter(fullFilter)
            setFilterOptions(constrainOptions({
                ...optionsByTags,
				tutors: filterTutorsByGender(optionsByTags.tutors, fullFilter.tutorGender),
                tgAccounts: initialOptions.tgAccounts,
                tags: optionsByTg.tags,
                students: [],
            }, fullFilter))
            pendingChangeRef.current = {
                key: getIdsKey(fullFilter),
                field: 'userIds',
            }
            onChange(fullFilter)
        }
        catch(error){
            if(error instanceof DOMException && error.name === 'AbortError') return

            console.log(error)
            if(error instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else if(error instanceof MyError){
                setGlobalMessage({message: error.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при заполнении фильтров пробных занятий', type: 'error'})
            }
        }
        finally{
            if(abortControllerRef.current === controller) setIsSelectingAll(false)
        }
    }

    const normalizedFilter = normalizeTrialFilter(filter)
    const isFullFilterSelected = Boolean(fullFilter)
        && hasSameIds(normalizedFilter.tgAccountIds, fullFilter?.tgAccountIds || [])
        && hasSameIds(normalizedFilter.tagIds, fullFilter?.tagIds || [])
        && hasSameIds(normalizedFilter.userIds, fullFilter?.userIds || [])
        && hasSameIds(normalizedFilter.statuses, fullFilter?.statuses || [])
        && normalizedFilter.tutorGender === fullFilter?.tutorGender

    return (
        <SurfaceBlock className={classes.container}>
            <section className={classes.actions}>
                <section className={classes.showAllButton}>
                    <MyButton
                        isLoading={stage !== null || isSelectingAll}
                        onClick={() => {
                            if(isFullFilterSelected) return
                            selectAllFilters()
                        }}
                    >
                        Показать все пробные
                    </MyButton>
                </section>
                <section className={classes.resetButton}>
                    <MyButton
                        onClick={() => {
                            pendingChangeRef.current = null
                            onChange(getDefaultTrialCalendarFilter())
                        }}
                    >
                        Сбросить фильтры
                    </MyButton>
                </section>
            </section>

            <section className={classes.field}>
                <span className={classes.label}>Рабочие Telegram</span>
                <DropDownListSelected
                    isLoading={stage === 'tgAccountIds'}
                    items={toItems(tgAccountOptions, item => item.title || `@${item.username}`)}
                    selectedIdItems={normalizedFilter.tgAccountIds}
                    onSelected={onSelected('tgAccountIds')}
                    onSelectAll={onSelectAll('tgAccountIds')}
                    selectedCount
                    search
                />
            </section>

            {Boolean(normalizedFilter.tgAccountIds.length) && (
                <section className={classes.field}>
                    <span className={classes.label}>Теги</span>
                    <DropDownListSelected
                        isLoading={stage === 'tagIds'}
                        items={toItems(tagOptions, item => item.name)}
                        selectedIdItems={normalizedFilter.tagIds}
                        onSelected={onSelected('tagIds')}
                        onSelectAll={onSelectAll('tagIds')}
                        selectedCount
                        search
                        searchPlaceholder="Поиск тега"
                    />
                </section>
            )}

            {Boolean(normalizedFilter.tagIds.length) && (
                <section className={classes.field}>
					<span className={classes.label}>Пол исполнителя</span>
					<DropDownListSelected
						isLoading={false}
						items={GENDER_ITEMS}
						selectedIdItems={normalizedFilter.tutorGender === 'TUTOR_GENDER_UNSPECIFIED'
							? []
							: [normalizedFilter.tutorGender]
						}
						onSelected={item => selected => changeTutorGender(
							selected ? item.id : 'TUTOR_GENDER_UNSPECIFIED'
						)}
						placeholder="Выберите пол"
					/>
				</section>
			)}

			{normalizedFilter.tutorGender !== 'TUTOR_GENDER_UNSPECIFIED' && Boolean(normalizedFilter.tagIds.length) && (
				<section className={classes.field}>
					<span className={classes.label}>Исполнители</span>
                    <DropDownListSelected
                        isLoading={stage === 'userIds'}
                        items={tutorOptions.map(tutor => ({id: tutor.userId, name: tutor.fullName, secondaryText: formatTutorLoad(tutor)}))}
                        selectedIdItems={normalizedFilter.userIds}
                        onSelected={onSelected('userIds')}
                        onSelectAll={onSelectAll('userIds')}
                        selectedCount
                        search
                    />
                </section>
            )}

            <section className={classes.field}>
                <span className={classes.label}>Статусы</span>
                <DropDownListSelected
                    isLoading={false}
                    items={STATUS_ITEMS}
                    selectedIdItems={getStatusChoices(normalizedFilter)}
                    onSelected={onStatusSelected}
                    onSelectAll={changeStatuses}
                    selectedCount
                />
            </section>
        </SurfaceBlock>
    )
}
